<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ pageTitle() }}</h2>
            <p>Create and organize navigation menus and submenus.</p>
        </div>
        <p-button label="Add Menu" icon="pi pi-plus" (onClick)="openAddRoot()" />
    </div>

    <div class="card">
        @if (loading()) {
            <div class="state"><i class="pi pi-spin pi-spinner"></i> Loading menus...</div>
        } @else if (!tree().length) {
            <div class="state">
                <i class="pi pi-inbox"></i>
                <p>No menus yet. Click <strong>Add Menu</strong> to create your first one.</p>
            </div>
        } @else {
            <div class="tree-head">
                <span class="col-title">Title</span>
                <span class="col-route">Route</span>
                <span class="col-perm">Permission</span>
                <span class="col-status">Status</span>
                <span class="col-actions">Actions</span>
            </div>

            @for (parent of tree(); track parent.menuId) {
                <!-- parent row -->
                <div class="tree-row parent">
                    <span class="col-title">
                        <button type="button" class="expander"
                            [class.invisible]="!parent.children?.length"
                            (click)="toggleExpand(parent.menuId)">
                            <i class="pi" [class.pi-chevron-down]="isExpanded(parent.menuId)"
                                [class.pi-chevron-right]="!isExpanded(parent.menuId)"></i>
                        </button>
                        <i class="menu-icon" [class]="parent.icon || 'pi pi-circle'"></i>
                        <span class="title-text">{{ parent.title }}</span>
                    </span>
                    <span class="col-route">{{ parent.route || '—' }}</span>
                    <span class="col-perm">{{ parent.permissionCode || '—' }}</span>
                    <span class="col-status">
                        <p-tag [value]="parent.isActive ? 'Active' : 'Inactive'"
                            [severity]="parent.isActive ? 'success' : 'secondary'" />
                    </span>
                    <span class="col-actions">
                        <p-button icon="pi pi-plus" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Add submenu" (onClick)="openAddChild(parent)" />
                        <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Edit" (onClick)="openEdit(parent)" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                            pTooltip="Delete" (onClick)="remove(parent)" />
                    </span>
                </div>

                <!-- children -->
                @if (isExpanded(parent.menuId)) {
                    @for (child of parent.children || []; track child.menuId) {
                        <div class="tree-row child">
                            <span class="col-title">
                                <span class="child-line"></span>
                                <i class="menu-icon" [class]="child.icon || 'pi pi-circle'"></i>
                                <span class="title-text">{{ child.title }}</span>
                            </span>
                            <span class="col-route">{{ child.route || '—' }}</span>
                            <span class="col-perm">{{ child.permissionCode || '—' }}</span>
                            <span class="col-status">
                                <p-tag [value]="child.isActive ? 'Active' : 'Inactive'"
                                    [severity]="child.isActive ? 'success' : 'secondary'" />
                            </span>
                            <span class="col-actions">
                                <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                                    pTooltip="Edit" (onClick)="openEdit(child)" />
                                <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                                    pTooltip="Delete" (onClick)="remove(child)" />
                            </span>
                        </div>
                    }
                }
            }
        }
    </div>
</div>

<!-- ===================== ADD / EDIT MODAL ===================== -->
<p-dialog [visible]="showModal()" (visibleChange)="showModal.set($event)" [modal]="true"
    [draggable]="true" [resizable]="false" [closable]="true" [dismissableMask]="false"
    [header]="modalTitle()" styleClass="menu-dialog" [style]="{ width: '480px' }"
    [breakpoints]="{ '480px': '94vw' }">
    <form [formGroup]="form" class="menu-form">
        <div class="field">
            <label>Title <span class="req">*</span></label>
            <input pInputText type="text" formControlName="title" placeholder="e.g. Partners">
            @if (form.get('title')?.touched && form.get('title')?.invalid) {
                <small class="err">Title is required</small>
            }
        </div>

        <div class="field">
            <label>Parent Menu</label>
            <p-select formControlName="parentId" [options]="parentOptions()" optionLabel="label"
                optionValue="value" [filter]="true" filterBy="label" [showClear]="true"
                placeholder="No parent (top level)" appendTo="body" />
            <small class="hint-muted">Pick a parent to make this a submenu, or leave as top level.</small>
        </div>

        <div class="grid-2">
            <div class="field">
                <label>Route</label>
                <input pInputText type="text" formControlName="route" placeholder="/partners">
            </div>
            <div class="field">
                <label>Icon</label>
                <button type="button" class="icon-select" (click)="openIconPicker()">
                    <i [class]="selectedIcon"></i>
                    <span class="mono">{{ selectedIcon }}</span>
                    <i class="pi pi-chevron-down chev"></i>
                </button>
            </div>
        </div>

        <div class="grid-2">
            <div class="field">
                <label>Display Order</label>
                <input pInputText type="number" formControlName="displayOrder" min="0">
            </div>
            <div class="field">
                <label>Permission Code</label>
                <input pInputText type="text" formControlName="permissionCode" placeholder="MENU_PARTNERS">
            </div>
        </div>

        <p-checkbox formControlName="isActive" [binary]="true" inputId="isActive" label="Active" />

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closeModal()" />
            <p-button [label]="saving() ? 'Saving...' : 'Save'" icon="pi pi-check"
                [loading]="saving()" (onClick)="save()" />
        </div>
    </form>
</p-dialog>

<!-- ===================== ICON PICKER MODAL ===================== -->
<p-dialog [visible]="showIconPicker()" (visibleChange)="showIconPicker.set($event)" [modal]="true"
    [draggable]="true" [resizable]="false" header="Choose an icon" styleClass="menu-dialog"
    [style]="{ width: '460px' }" [breakpoints]="{ '480px': '94vw' }">
    <div class="icon-picker">
        <p-iconfield iconPosition="left" class="search-field">
            <p-inputicon styleClass="pi pi-search" />
            <input pInputText type="text" placeholder="Search icons..." [ngModel]="iconSearch()"
                (ngModelChange)="iconSearch.set($event)">
        </p-iconfield>
        @if (filteredIcons().length) {
            <div class="icon-grid">
                @for (cls of filteredIcons(); track cls) {
                    <button type="button" class="icon-cell" [class.on]="cls === selectedIcon"
                        [title]="cls" (click)="pickIcon(cls)">
                        <i [class]="cls"></i>
                    </button>
                }
            </div>
        } @else {
            <p class="no-icons">No icons match "{{ iconSearch() }}".</p>
        }
    </div>
</p-dialog>
